import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  BookingParameters,
  BookingParametersInterface,
  copyBookingParameters
} from '../../shared/entities/bookingParameters';
import { BROWSER_WINDOW } from './analytics.service';
import { EnvironmentService } from './environment.service';
import { LocalStorageService } from './local-storage.service';
import { SessionStorageService } from './session-storage.service';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';

@Injectable({
  providedIn: 'root'
})
export class BookingParameterService {
  bookingParameters: BookingParameters;

  private localStorageName = 'bookingParameters';
  private isServer = isPlatformServer(this.platformId);

  constructor(
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService,
    private environmentService: EnvironmentService,
    private platformService: PlatformInformationService,
    @Inject(BROWSER_WINDOW) protected window,
    @Inject(PLATFORM_ID) protected platformId: object
  ) {
    if (this.window && !this.window.name) {
      this.window.name =
        '_' +
        Math.random()
          .toString(36)
          .substring(2, 2 + 9);
    }
    this.bookingParameters = this.getBookingParameters();
  }

  createNewBookingParameters(): BookingParameters {
    const range = this.getDefaultDateRange();
    const params = new BookingParameters('', range.start, range.end, []);
    params.userDefinedSearch = false;

    return params;
  }

  getBookingParameters(): BookingParameters {
    if (this.bookingParameters) {
      return this.bookingParameters;
    }

    let params = this.getBookingParametesFromSession();
    if (!params) {
      params = this.getBookingParametersFromStorage();
    }
    params.partner = this.platformService.getCurrentPartner();
    return params;
  }

  saveBookingParameters(params: BookingParametersInterface, time?: number) {
    this.bookingParameters.update(params);
    if (!this.isServer) {
      const now = time ? time : new Date().getTime();

      this.sessionStorage.write(
        this.localStorageName,
        JSON.stringify({ timestamp: now, bookingParameters: this.bookingParameters })
      );

      // never store shopID to the local-storage
      const bookingParamsLocalStorage = this.bookingParameters.copy();
      delete bookingParamsLocalStorage.shopId;

      this.localStorage.write(
        this.localStorageName,
        JSON.stringify({ timestamp: now, bookingParameters: bookingParamsLocalStorage })
      );
    }
  }

  /**
   * loads bookingParameter from the session storage. Will be null if:
   * browser doesn't support session storage
   * session storage data is too old
   * session has no bookingParameters currently
   * session was copied from an other window (stupid browsers, stealing session on new tab)
   */
  private getBookingParametesFromSession(): null | BookingParameters {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const session = this.sessionStorage.read(this.localStorageName);
    if (session !== null) {
      const data = JSON.parse(session);
      if (now.getTime() - 5184000000 <= data.timestamp) {
        if (this.isServer || (this.window && data.bookingParameters.windowname === this.window.name)) {
          const bookingParameters = copyBookingParameters(data.bookingParameters);
          bookingParameters.start = new Date(bookingParameters.start);
          bookingParameters.end = new Date(bookingParameters.end);
          // start/end date still usefull?
          if (
            bookingParameters.start.getTime() < now.getTime() ||
            bookingParameters.end.getTime() < now.getTime()
          ) {
            const start_end = this.getDefaultDateRange();
            bookingParameters.start = start_end.start;
            bookingParameters.end = start_end.end;
          }
          if (typeof bookingParameters.ages === 'undefined') {
            bookingParameters.ages = new Array<number>();
          }
          return bookingParameters;
        }
      }
      // too old, drop it
      return null;
    }
    // none found
    return null;
  }

  /**
   * loads bookingParameters from session storage. Will return default bookingParams if none found
   * and will tweak the start/end date to something useful if the given range is in the past
   */
  private getBookingParametersFromStorage(): BookingParameters {
    let bookingParameters: BookingParameters;
    const localStorageBookingParameters = this.localStorage.read(this.localStorageName);
    if (localStorageBookingParameters !== null) {
      const data = JSON.parse(localStorageBookingParameters);
      // storage not older than 60 days?
      if (new Date().getTime() - 5184000000 <= data.timestamp) {
        bookingParameters = copyBookingParameters(data.bookingParameters);
        bookingParameters.start = new Date(bookingParameters.start);
        bookingParameters.end = new Date(bookingParameters.end);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (
          bookingParameters.start.getTime() < now.getTime() ||
          bookingParameters.end.getTime() < now.getTime()
        ) {
          const start_end = this.getDefaultDateRange();
          bookingParameters.start = start_end.start;
          bookingParameters.end = start_end.end;
        }
      }
    }
    // no bookingParameters in storage found, create a new one
    if (!bookingParameters) {
      bookingParameters = this.createNewBookingParameters();
    }
    // don't use partner, it will deactivate the search on all pages
    bookingParameters.partner = null;
    if (bookingParameters.cid) {
      // inform an event that there was a persisted cid which could be used to "welcome back!" the user on the start or townpage
      bookingParameters.cid = null;
    }

    // remove the window from the storage
    bookingParameters.windowname = this.window?.name;

    return bookingParameters;
  }

  /**
   * returns the fallback daterange to show in the datepicker depending on on/off season settings
   */
  private getDefaultDateRange(): { start: Date; end: Date } {
    const now = new Date();
    const summer_start = new Date(
      now.getFullYear() + '-' + this.environmentService.getEnv().datepicker.summer_start
    );
    const summer_end = new Date(
      now.getFullYear() + '-' + this.environmentService.getEnv().datepicker.summer_end
    );

    let start: Date;
    let end: Date;

    if (now >= summer_start && now <= summer_end) {
      // its summer, set the summer daterange
      start = new Date(
        now.getFullYear() + '-' + this.environmentService.getEnv().datepicker.summer_default_date
      );
      end = new Date(
        now.getFullYear() + '-' + this.environmentService.getEnv().datepicker.summer_default_date
      );
      end.setDate(start.getDate() + (this.environmentService.getEnv().datepicker.summer_range_of_days - 1));
    } else {
      // set the regular daterange
      start = new Date();
      end = new Date();
      let daysToAdd = 7 - start.getDay();
      daysToAdd += daysToAdd <= 2 ? 7 : 0; // Fr, Sa, Su of the week? skip to next Su
      start.setDate(start.getDate() + daysToAdd);
      end.setDate(end.getDate() + daysToAdd + this.environmentService.getEnv().datepicker.range_of_days);
    }
    start.setHours(0, 0, 0);
    end.setHours(0, 0, 0);
    return { start: start, end: end };
  }
}
